/**
 * The ring, as the application sees it: mount it, hand it data, hear about
 * selection, and keep the pin store in step.
 *
 * The scene core owns hit-testing, layout and dragging. This wrapper only
 * consumes its public surface — the `circle:select` event, `setPins`,
 * `visibleEventList` — and translates scene entries into application terms
 * ("a day", "an entry", "a cluster") so pages never reach into the scene.
 */
import type { CalEvent, EventPin, ViewCamera, ViewLayout, ViewState } from '@circle/shared';
import { Scene, type SceneBundle, type SceneEntry } from '../core/scene';
import { InputController, computeFitWithCards, type ViewportInsets } from '../core/input';
import { ViewStateUrl } from '../core/viewstate';
import { effectivePins, type PinStore } from '../core/pins';
import { floatSizing } from '../fragments/types';
import { Tooltip, describeEntry } from './tooltip';

/**
 * What the ring needs of a bundle: what the scene draws, plus the owner's
 * per-circle pins. A `CalendarBundle` is one; so is a shared bundle once its
 * sources carry a `calendarId`, without the page pretending it is the whole
 * owner's record.
 */
export type RingBundle = SceneBundle & { eventPins?: EventPin[] };

export type Selection =
  | { kind: 'day'; month: number; day: number }
  | { kind: 'event'; eventId: string }
  | { kind: 'cluster'; eventIds: string[] }
  | { kind: 'month'; month: number }
  /** A band, or a circle's own ring line, was clicked (docs/REVIEW.md 1.14). */
  | { kind: 'circle'; tagId: string }
  | null;

/** `detail` of the scene's `circle:select` event. */
export interface SelectDetail {
  id: string | null;
  kind: string | null;
  eventIds: string[];
  /** The circle a band or a ring line stands for; null for the root and the rest. */
  tagId?: string | null;
  /** The ring the fragment is drawn on: `'root'` or a tag id. */
  ringId?: string | null;
}

export interface RingViewOptions {
  host: HTMLElement;
  bundle: RingBundle;
  today?: Date;
  /** Owner: a RemotePinStore. Viewer: a LocalPinStore. */
  pins?: PinStore;
  /** The view's layout of the circle tree. Absent = the root, merged. */
  layout?: ViewLayout;
  /**
   * The camera the view was saved with, if it kept one. It is the middle step
   * of the opening camera's precedence: the URL hash first, this next, and a
   * fit of whatever `layout` turned out to cover last.
   */
  camera?: ViewCamera;
  onSelect?: (selection: Selection) => void;
  /**
   * A card was let go over a band or over a circle's ring line: the entry wants
   * to join that circle. The ring does not change the data — the page does,
   * with `api.setEventTags` — and a reload is what puts the mark on the new
   * ring.
   */
  onDropOnCircle?: (eventId: string, tagId: string) => void;
  onYearChange?: (year: number) => void;
  /**
   * The camera has come to rest somewhere. Only on settle, not per frame: this
   * is for whoever has to notice that where you are looking from is no longer
   * where the view was saved from.
   */
  onCamera?: () => void;
  /** A drag or a reset could not be saved. */
  onPinError?: (error: unknown) => void;
  /**
   * What the chrome is covering right now, asked afresh every time the camera
   * is fitted — the panel opens and shuts without the ring hearing about it, so
   * a remembered answer would be the last one rather than the true one.
   */
  insets?: () => ViewportInsets;
}

/**
 * Scene selection detail → what the application understands.
 *
 * A band and a circle's own ring line both stand for a circle rather than for
 * an entry, so they come out as `{ kind: 'circle', tagId }` and the page does
 * with the circle what a click on its row in the panel would do. The root's
 * ring carries no tag and stands for the calendar, so it selects nothing.
 */
export function translate(detail: SelectDetail): Selection {
  if (detail.tagId && (detail.kind === 'band' || detail.kind === 'ring')) {
    return { kind: 'circle', tagId: detail.tagId };
  }
  switch (detail.kind) {
    case 'day': {
      const [, month, day] = (detail.id ?? '').split(':');
      return month && day ? { kind: 'day', month: Number(month), day: Number(day) } : null;
    }
    case 'month': {
      const [, month] = (detail.id ?? '').split(':');
      return month ? { kind: 'month', month: Number(month) } : null;
    }
    case 'cluster':
      return { kind: 'cluster', eventIds: detail.eventIds };
    case 'ringMark':
    case 'leader':
    case 'float':
      return detail.eventIds[0] ? { kind: 'event', eventId: detail.eventIds[0] } : null;
    default:
      return null;
  }
}

export class RingView {
  readonly scene: Scene;
  readonly input: InputController;
  private readonly url: ViewStateUrl;
  private readonly tooltip: Tooltip;
  private readonly host: HTMLElement;
  private readonly today: Date;
  private readonly pins: PinStore | undefined;
  private bundle: RingBundle;
  private size = { width: 1, height: 1 };
  private readonly onYearChange: ((year: number) => void) | undefined;
  private readonly onSelect: ((selection: Selection) => void) | undefined;
  private readonly onCamera: (() => void) | undefined;
  private readonly askInsets: (() => ViewportInsets) | undefined;
  /** The circle the chrome has lit, so a re-render can put the light back. */
  private litRing: string | null = null;

  constructor(options: RingViewOptions) {
    this.host = options.host;
    this.bundle = options.bundle;
    this.today = options.today ?? new Date();
    this.pins = options.pins;
    this.onYearChange = options.onYearChange;
    this.onSelect = options.onSelect;
    this.onCamera = options.onCamera;
    this.askInsets = options.insets;

    this.scene = new Scene(this.host);
    this.tooltip = new Tooltip(this.host);

    this.size = this.measure();
    this.scene.resize(this.size.width, this.size.height);
    // The layout goes in **first**, and without a tween: there is nothing on
    // screen to tween from. With no bundle yet this only files the layout away;
    // the render below is what lays the tree out from it. The camera has to
    // cover the rings where the view puts them — fitting the root ring alone
    // left three quarters of a split layout off the edge of the screen, which
    // is what made drag-to-pin look dead after a reload — and `Scene.measure`
    // answers that from the layout alone, so nothing is drawn twice.
    if (options.layout) this.scene.setViewLayout(options.layout, { animate: false });

    // Opening camera, in order of who has the better claim to it: the hash,
    // because it is this browser's own last look at the ring or a link somebody
    // sent; then the camera the view was saved with; then a fit of what the
    // layout above actually covers. `parseViewState` falls back per field, so a
    // hash carrying only a year still takes the view's camera under it.
    // No insets here: the panel that would supply them is built after the ring.
    const fitted: ViewState = {
      year: this.today.getFullYear(),
      rotation: 0,
      ...computeFitWithCards(
        this.size.width,
        this.size.height,
        Scene.measure(this.bundle, options.layout),
        {},
        floatSizing(this.scene.currentTheme),
      ),
    };
    const defaults: ViewState = options.camera ? { ...fitted, ...options.camera } : fitted;

    this.url = new ViewStateUrl(defaults, {
      onExternalChange: (view) => {
        const yearChanged = view.year !== this.input.currentView.year;
        this.input.setView(view, false);
        // A hash change is a jump, not a gesture: it has already settled.
        this.input.settle();
        this.onCamera?.();
        if (yearChanged) {
          this.scene.render(this.bundle, view, this.today);
          this.applyPins();
          this.onYearChange?.(view.year);
        }
      },
    });

    const initial = this.url.read();
    this.scene.render(this.bundle, initial, this.today);
    this.applyPins();

    this.input = new InputController({
      scene: this.scene,
      initial,
      onView: (view, committed) => {
        if (!committed) return;
        this.url.write(view);
        this.onCamera?.();
      },
      onHover: (entry, screen) => this.tooltip.show(entry ? this.describe(entry) : '', screen),
      // A card carries the ring it came off, so a pin made on one circle's ring
      // stays that circle's and does not move the event everywhere else.
      onPin: (eventId, pin, ringId) => {
        this.pins?.set(eventId, ringId === 'root' ? null : ringId, pin);
      },
      ...(options.onDropOnCircle ? { onDropOnCircle: options.onDropOnCircle } : {}),
    });
    this.input.setViewport(this.size.width, this.size.height);
    this.input.setView(initial, false);

    this.scene.svg.addEventListener('circle:select', this.onSceneSelect as EventListener);
    window.addEventListener('resize', this.onResize);
  }

  get year(): number {
    return this.input.currentView.year;
  }

  setYear(year: number): void {
    const view = { ...this.input.currentView, year };
    this.input.setView(view, true);
    this.scene.render(this.bundle, view, this.today);
    this.applyPins();
    this.onYearChange?.(year);
  }

  /** Swap in new data without disturbing the camera. */
  setBundle(bundle: RingBundle): void {
    this.bundle = bundle;
    this.scene.render(bundle, this.input.currentView, this.today);
    this.applyPins();
    this.relight();
  }

  /**
   * Show only the entries the search kept; `null` shows all of them again.
   *
   * This and `highlightRing` are two filters on one ring and they do not fight,
   * because they act on different things. The search takes a matchless entry
   * out of the **float layout** — no card, a dimmed mark — so the matches get
   * the room. A lit circle is a **decoration** in CSS, keyed on the `data-ring`
   * the scene stamps on every fragment, and touches the layout not at all.
   * They compose: the cards left are the matches, and the light picks the
   * circle out among them. The only crossing is that re-filtering rebuilds the
   * marks and drops the classes with them, which is why the light is put back
   * here rather than left to the caller.
   */
  setFilter(keep: ((event: CalEvent) => boolean) | null): void {
    this.scene.setFilter(keep);
    this.relight();
  }

  /**
   * Drop every pin. A viewer's `clear()` empties their local store and the
   * owner's arrangement shows through again. An owner passes `serverReset`
   * once the page has already made the one scoped reset request: the store
   * then only forgets its cache, and the `layout` the server sent with each
   * event is dropped too, since the server's pins are gone as well.
   */
  resetPins(opts: { serverReset?: boolean } = {}): void {
    if (opts.serverReset) {
      this.pins?.forget();
      for (const event of this.bundle.events) event.layout = null;
    } else {
      this.pins?.clear();
    }
    this.applyPins();
  }

  applyPins(): void {
    this.scene.setPins(
      effectivePins(this.scene.visibleEventList, this.pins, this.bundle.eventPins),
    );
  }

  // --- circles --------------------------------------------------------------

  /**
   * Re-lay the tree out. `animate` is the big bang; without it the rings jump,
   * which is what a fresh page or a jump-cut wants.
   */
  setViewLayout(layout: ViewLayout, opts: { animate?: boolean } = {}): void {
    // Splitting the root throws rings out onto an orbit, and what the camera
    // has to cover roughly doubles. Someone looking at the whole ring wants to
    // keep looking at the whole thing; someone who has zoomed in on March has
    // chosen where they are, and is left there.
    const wasFitted = this.isFitted();
    this.scene.setViewLayout(layout, opts);
    this.applyPins();
    if (wasFitted) this.refit();
  }

  /** What the chrome is covering, or the whole viewport when nothing is. */
  private insets(override?: ViewportInsets): ViewportInsets {
    return override ?? this.askInsets?.() ?? {};
  }

  /** The camera that fits every ring, and its cards, into the free viewport. */
  private fitCamera(insets?: ViewportInsets): ViewCamera {
    const fit = computeFitWithCards(
      this.size.width,
      this.size.height,
      this.scene.boundsRadius,
      this.insets(insets),
      floatSizing(this.scene.currentTheme),
    );
    return { ...fit, rotation: this.input.currentView.rotation };
  }

  /** True while the camera is showing everything rather than a part of it. */
  private isFitted(): boolean {
    return this.input.currentView.scale <= this.fitCamera().scale * 1.05;
  }

  private refit(): void {
    // `setViewport` is what recomputes the zoom clamp from the new bounds.
    this.input.setInsets(this.insets());
    this.input.setViewport(this.size.width, this.size.height);
    const fit = this.fitCamera();
    this.input.setView({ ...this.input.currentView, ...fit }, true);
    this.input.settle();
  }

  viewLayout(): ViewLayout {
    return this.scene.viewLayout();
  }

  /** Every ring an entry is drawn on: one in `merged`, several in `split`. */
  ringsFor(eventId: string): string[] {
    return this.scene.ringsFor(eventId);
  }

  /**
   * Light one circle's ring up from outside the scene.
   *
   * The scene stamps `data-ring` on everything it draws, which is all the
   * chrome needs: this marks the matching elements `is-lit` and puts the `<svg>`
   * into the state where everything else recedes. It is a decoration, not
   * state — the next re-render of a fragment drops it, which is right, because
   * the panel that asked for it will have gone by then.
   */
  highlightRing(ringId: string | null): void {
    this.litRing = ringId;
    this.relight();
  }

  /** Put the light back on whatever a re-render or a re-filter took it off. */
  private relight(): void {
    const ringId = this.litRing;
    const svg = this.scene.svg;
    svg.classList.toggle('is-ring-lit', Boolean(ringId));
    for (const node of svg.querySelectorAll('[data-ring]')) {
      node.classList.toggle('is-lit', ringId !== null && node.getAttribute('data-ring') === ringId);
    }
  }

  /**
   * Pan just enough that the selected thing is inside the free area.
   *
   * On a phone the detail sheet opens over the bottom of the ring, which is
   * often exactly where the mark just tapped is. A fit is only consulted when
   * the camera is fitted; this runs on open whatever the camera is doing, and
   * moves it by the smallest amount that brings the selection clear of the
   * chrome — nothing at all when it already is. `margin` is the room left
   * round the mark, and covers the sheet's own entry slide.
   */
  revealSelection(insets: ViewportInsets = this.insets(), margin = 28): void {
    const id = this.input.selection;
    const entry = id ? this.scene.entry(id) : undefined;
    if (!entry) return;
    const host = this.host.getBoundingClientRect();
    const box = entry.el.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) return;
    const free = {
      left: (insets.left ?? 0) + margin,
      top: (insets.top ?? 0) + margin,
      right: this.size.width - (insets.right ?? 0) - margin,
      bottom: this.size.height - (insets.bottom ?? 0) - margin,
    };
    if (free.right <= free.left || free.bottom <= free.top) return;
    const x = box.left + box.width / 2 - host.left;
    const y = box.top + box.height / 2 - host.top;
    const dx = x < free.left ? free.left - x : x > free.right ? free.right - x : 0;
    const dy = y < free.top ? free.top - y : y > free.bottom ? free.bottom - y : 0;
    if (dx === 0 && dy === 0) return;
    const view = this.input.currentView;
    this.input.setView({ ...view, tx: view.tx + dx, ty: view.ty + dy }, true);
    this.input.settle();
  }

  /** What a saved view would record: the camera as it is now. */
  camera(): ViewCamera {
    const view = this.input.currentView;
    return { scale: view.scale, tx: view.tx, ty: view.ty, rotation: view.rotation };
  }

  /** Put the camera back where a saved view left it. */
  setCamera(camera: ViewCamera): void {
    this.input.setView({ ...this.input.currentView, ...camera }, true);
    this.input.settle();
  }

  /**
   * Fit every ring, whatever the layout has made of the bounds, into whatever
   * the chrome has left. `insets` overrides what the page's own getter says,
   * for a caller that knows better.
   */
  fit(insets?: ViewportInsets): void {
    this.input.setInsets(this.insets(insets));
    this.input.setViewport(this.size.width, this.size.height);
    this.input.reset();
  }

  select(selection: Selection): void {
    if (!selection) {
      this.input.select(null);
      return;
    }
    if (selection.kind === 'event') {
      this.input.select(this.scene.ownerFor(selection.eventId) ?? `ringMark:${selection.eventId}`);
    } else if (selection.kind === 'day') {
      this.input.select(`day:${selection.month}:${selection.day}`);
    } else if (selection.kind === 'month') {
      this.input.select(`month:${selection.month}`);
    }
  }

  resetView(insets?: ViewportInsets): void {
    this.fit(insets);
  }

  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    this.scene.svg.removeEventListener('circle:select', this.onSceneSelect as EventListener);
    this.input.destroy();
    this.url.dispose();
    this.tooltip.destroy();
    this.scene.svg.remove();
  }

  private readonly onSceneSelect = (event: CustomEvent<SelectDetail>): void => {
    this.onSelect?.(translate(event.detail));
  };

  private measure(): { width: number; height: number } {
    return {
      width: this.host.clientWidth || window.innerWidth,
      height: this.host.clientHeight || window.innerHeight,
    };
  }

  private readonly onResize = (): void => {
    const next = this.measure();
    const view = this.input.currentView;
    const shifted: ViewState = {
      ...view,
      tx: view.tx + (next.width - this.size.width) / 2,
      ty: view.ty + (next.height - this.size.height) / 2,
    };
    this.size = next;
    this.scene.resize(next.width, next.height);
    this.input.setViewport(next.width, next.height);
    this.input.setView(shifted, true);
  };

  describe(entry: SceneEntry): string {
    return describeEntry(entry, { scene: this.scene, year: this.input.currentView.year, today: this.today });
  }
}
